import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import * as global from "../data/global";
import { saveSelectedSite } from "../data/global";
import { MuiMaterials } from "../@index.js";
import { createApiEndpoint } from "../index.js";
import ModuleViewerDialog from "./ModuleViewerDialog.js";
import { useModuleOutputToggles } from "./useModuleOutputToggles.js";
import {
    createWindowChromeTheme,
    getWindowChromeThemeColor,
    SiteSelectPhase,
    SiteWindowHeaderLeading,
    SiteWindowToolNav,
    ChromeHeaderBar,
    Spinner,
    SITE_WINDOW_IDS,
    ensureSiteInfosLoaded,
} from "./windowChrome/index.js";

function parseJsonPayload(raw) {
    let parsed = raw;
    for (let depth = 0; depth < 3 && typeof parsed === "string"; depth += 1) {
        try {
            parsed = JSON.parse(parsed);
        } catch {
            return null;
        }
    }
    return parsed && typeof parsed === "object" ? parsed : null;
}

export default function ModuleViewerWindow() {
    const navigate = useNavigate();
    const location = useLocation();
    const autoLoadHandledRef = useRef(false);

    const [forceUpdate, setForceUpdate] = useState(0);
    const [phase, setPhase] = useState("select");
    const [sites, setSites] = useState([]);
    const [sitesLoading, setSitesLoading] = useState(true);
    const carriedSiteGuid =
        (location.state?.autoLoad && location.state?.siteGuid) || location.state?.siteGuid || "";
    const [pickerSiteGuid, setPickerSiteGuid] = useState(() => carriedSiteGuid || "");
    const [loadedSiteGuid, setLoadedSiteGuid] = useState("");
    const [loading, setLoading] = useState(() => Boolean(carriedSiteGuid && location.state?.autoLoad));
    const [outputsLocked, setOutputsLocked] = useState(false);
    const outputsLockedRef = useRef(false);
    const [status, setStatus] = useState("");
    const {
        modulePoints,
        pendingOutputToggles,
        toggleOutput: toggleModuleOutput,
        applyPolledSession,
    } = useModuleOutputToggles({ lockedRef: outputsLockedRef, onError: setStatus });

    const { theme, textPrimary, textSecondary, paper, border, pageBg } = useMemo(
        () => createWindowChromeTheme(),
        [forceUpdate],
    );

    const isAdmin = global.isAdminUser(global.gw?.email);

    useEffect(() => {
        if (!isAdmin) navigate("/sites", { replace: true });
    }, [isAdmin, navigate]);

    useEffect(() => {
        const handleThemeChange = () => setForceUpdate((prev) => prev + 1);
        window.addEventListener("themeChanged", handleThemeChange);
        return () => window.removeEventListener("themeChanged", handleThemeChange);
    }, []);

    useEffect(() => {
        let cancelled = false;
        const apply = () => {
            const all = [...(global.gw.siteInfos || [])].filter((s) => s.siteGuid && !s.isHms);
            all.sort((a, b) =>
                (a.siteName || "").toLowerCase().localeCompare((b.siteName || "").toLowerCase()),
            );
            if (!cancelled) {
                setSites(all);
                setSitesLoading(false);
            }
        };
        if ((global.gw.siteInfos || []).length > 0) {
            apply();
            return undefined;
        }
        ensureSiteInfosLoaded().then(() => {
            if (!cancelled) apply();
        });
        return () => {
            cancelled = true;
        };
    }, [forceUpdate]);

    useLayoutEffect(() => {
        if (!location.state?.autoLoad || !location.state?.siteGuid) return;
        setPickerSiteGuid(location.state.siteGuid);
    }, [location.state?.autoLoad, location.state?.siteGuid]);

    const applySiteSelection = useCallback(async (siteGuid) => {
        if (!siteGuid) return;
        setLoading(true);
        try {
            const want = global.normalizeGuidForCompare
                ? global.normalizeGuidForCompare(siteGuid)
                : String(siteGuid || "").toLowerCase();
            const info = (global.gw.siteInfos || []).find((s) => {
                const g = s?.siteGuid;
                if (!g) return false;
                const have = global.normalizeGuidForCompare
                    ? global.normalizeGuidForCompare(g)
                    : String(g).toLowerCase();
                return have === want;
            });

            if (info) {
                if (global.gw.selectedSite?.refreshId) {
                    clearTimeout(global.gw.selectedSite.refreshId);
                }
                if (
                    !global.gw.selectedSite
                    || global.normalizeGuidForCompare?.(global.gw.selectedSite.siteGuid)
                        !== global.normalizeGuidForCompare?.(info.siteGuid)
                ) {
                    global.gw.selectedSite = new global.Site();
                }
                global.gw.selectedSite.siteIndex = info.siteIndex;
                global.gw.selectedSite.siteGuid = info.siteGuid;
                global.gw.selectedSite.siteName = info.siteName;
                global.gw.selectedSite.lastScan = info.lastScan;
                saveSelectedSite?.();
            } else if (global.gw.selectedSite) {
                global.gw.selectedSite.siteGuid = siteGuid;
            }

            await global.initializeCustomerSite(navigate);
            const loadedName =
                global.gw.selectedSite?.siteName
                || global.gw.selectedSite?.main?.siteName
                || global.gw.selectedSite?.data?.main?.siteName;
            if (loadedName) global.gw.selectedSite.siteName = loadedName;

            setLoadedSiteGuid(info?.siteGuid || siteGuid);
            setPickerSiteGuid(info?.siteGuid || siteGuid);
            setPhase("view");
            setStatus("");
        } catch (e) {
            console.error("ModuleViewerWindow: failed to load site", e);
            setPhase("select");
            setLoadedSiteGuid("");
        } finally {
            setLoading(false);
        }
    }, [navigate]);

    useEffect(() => {
        const siteGuid = location.state?.siteGuid;
        if (!siteGuid || !location.state?.autoLoad) return;
        if (autoLoadHandledRef.current === siteGuid) return;
        autoLoadHandledRef.current = siteGuid;
        setPickerSiteGuid(siteGuid);
        applySiteSelection(siteGuid);
    }, [location.state, applySiteSelection]);

    useEffect(() => {
        if (phase !== "view" || !loadedSiteGuid) return undefined;
        createApiEndpoint().fetchAddDiagnostic("direct", JSON.stringify({ action: "viewModules" })).catch(() => {});
        return undefined;
    }, [phase, loadedSiteGuid]);

    useEffect(() => {
        return () => {
            createApiEndpoint().fetchAddDiagnostic("direct", JSON.stringify({ action: "hideModules" })).catch(() => {});
        };
    }, []);

    const poll = useCallback(async () => {
        if (phase !== "view" || !global.gw.selectedSite?.siteGuid) return;
        try {
            const requestedAt = Date.now();
            const response = await createApiEndpoint().fetchDiagnostic("direct");
            const session = parseJsonPayload(response?.data?.getDiagnostic);
            if (!session) return;
            outputsLockedRef.current = Boolean(session.isRunning);
            setOutputsLocked(Boolean(session.isRunning));
            applyPolledSession(session, requestedAt);
        } catch (err) {
            console.warn("ModuleViewerWindow: poll", err);
        }
    }, [phase, applyPolledSession]);

    useEffect(() => {
        if (phase !== "view") return undefined;
        poll();
        const id = window.setInterval(poll, pendingOutputToggles.length > 0 ? 50 : 250);
        return () => window.clearInterval(id);
    }, [phase, poll, loadedSiteGuid, pendingOutputToggles.length]);

    useEffect(() => {
        if (phase !== "view") return undefined;
        const beat = () => {
            createApiEndpoint().fetchAddDiagnostic("direct", JSON.stringify({ action: "heartbeat" })).catch(() => {});
        };
        beat();
        const id = window.setInterval(beat, 5000);
        const handlePageHide = () => {
            createApiEndpoint().fetchAddDiagnostic("direct", JSON.stringify({ action: "hideModules" })).catch(() => {});
        };
        window.addEventListener("pagehide", handlePageHide);
        return () => {
            window.clearInterval(id);
            window.removeEventListener("pagehide", handlePageHide);
        };
    }, [phase, loadedSiteGuid]);

    const handleClose = () => {
        createApiEndpoint().fetchAddDiagnostic("direct", JSON.stringify({ action: "hideModules" })).catch(() => {});
        navigate("/sites", { replace: true });
    };

    const handleContinue = (guid) => {
        const next = guid || pickerSiteGuid;
        if (!next) return;
        setPickerSiteGuid(next);
        applySiteSelection(next);
    };

    const handleHeaderSiteChange = (siteGuid) => {
        if (!siteGuid || siteGuid === loadedSiteGuid) return;
        setPickerSiteGuid(siteGuid);
        applySiteSelection(siteGuid);
    };

    const activeSiteGuid = loadedSiteGuid || pickerSiteGuid || "";

    if (!isAdmin) return null;

    return (
        <MuiMaterials.ThemeProvider theme={theme}>
            <div
                style={{
                    position: "fixed",
                    inset: 0,
                    zIndex: 1000,
                    display: "flex",
                    flexDirection: "column",
                    background: pageBg,
                    color: textPrimary,
                    paddingTop: "env(safe-area-inset-top, 0px)",
                    paddingBottom: "env(safe-area-inset-bottom, 0px)",
                }}
            >
                {loading ? (
                    <MuiMaterials.Box
                        sx={{
                            position: "absolute",
                            inset: 0,
                            zIndex: 2,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            backgroundColor: getWindowChromeThemeColor("rgba(232,232,232,0.7)", "rgba(26,26,26,0.7)"),
                        }}
                    >
                        <Spinner size="modal" />
                    </MuiMaterials.Box>
                ) : null}

                {phase === "select" ? (
                    <ChromeHeaderBar
                        paper={paper}
                        border={border}
                        textSecondary={textSecondary}
                        onClose={handleClose}
                        tool={
                            <SiteWindowToolNav
                                toolTitle="Module Viewer"
                                siteWindowId={SITE_WINDOW_IDS.moduleViewer}
                                siteGuid={null}
                                textSecondary={textSecondary}
                            />
                        }
                    />
                ) : null}

                {phase === "select" ? (
                    <SiteSelectPhase
                        value={pickerSiteGuid}
                        onChange={setPickerSiteGuid}
                        onContinue={handleContinue}
                        onCancel={handleClose}
                        loading={sitesLoading || loading}
                        filterSite={(s) => !s.isHms}
                    />
                ) : null}

                {phase === "view" ? (
                    <>
                        <ChromeHeaderBar
                            paper={paper}
                            border={border}
                            textSecondary={textSecondary}
                            onClose={handleClose}
                            leading={
                                <SiteWindowHeaderLeading
                                    toolTitle="Module Viewer"
                                    siteWindowId={SITE_WINDOW_IDS.moduleViewer}
                                    siteGuid={activeSiteGuid || null}
                                    textSecondary={textSecondary}
                                    border={border}
                                    showSiteSelect
                                    siteSelectValue={activeSiteGuid}
                                    onSiteChange={handleHeaderSiteChange}
                                    sites={sites}
                                    filterSite={(s) => !s.isHms}
                                    siteSelectDisabled={loading || sitesLoading}
                                    includeTool={false}
                                />
                            }
                            tool={
                                <SiteWindowToolNav
                                    toolTitle="Module Viewer"
                                    siteWindowId={SITE_WINDOW_IDS.moduleViewer}
                                    siteGuid={activeSiteGuid || null}
                                    textSecondary={textSecondary}
                                />
                            }
                        />
                        {status ? (
                            <MuiMaterials.Typography sx={{ px: 2, py: 0.5, fontSize: 13, color: textSecondary }}>
                                {status}
                            </MuiMaterials.Typography>
                        ) : null}
                        <ModuleViewerDialog
                            open
                            onClose={handleClose}
                            devices={global.gw.selectedSite?.devices || []}
                            modules={global.gw.selectedSite?.modules || []}
                            modulePoints={modulePoints}
                            outputsLocked={outputsLocked}
                            pendingOutputToggles={pendingOutputToggles}
                            onToggleOutput={toggleModuleOutput}
                        />
                    </>
                ) : null}
            </div>
        </MuiMaterials.ThemeProvider>
    );
}
